// ─────────────────────────────────────────────────────────────────────────────
// CRM 2.0 — Saved Views hooks
//
// TanStack Query wrappers around the additive `/api/v2/views` endpoints. Uses the
// shared axios client (cookie auth) exactly like the other feature hooks.
// (Requirements 7.1, 7.2, 7.3, 7.5)
// ─────────────────────────────────────────────────────────────────────────────

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type {
  ApplyViewResponse,
  CreateViewResponse,
  SavedView,
  SavedViewsResponse,
  ViewEntityType,
  ViewFilters,
} from '../types';

/** Query key factory so every list scopes its cache by entity. */
export const savedViewsKey = (entityType: ViewEntityType) => ['saved-views', entityType] as const;

/** List the caller's own saved views for a given entity (owner-scoped, Req 7.4). */
export function useSavedViews(entityType: ViewEntityType) {
  return useQuery({
    queryKey: savedViewsKey(entityType),
    queryFn: async () => {
      const { data } = await api.get<SavedViewsResponse>(
        `/api/v2/views?entityType=${encodeURIComponent(entityType)}`,
      );
      return data.views;
    },
  });
}

/**
 * Create / apply / delete mutations for a given entity's saved views. Kept in one
 * hook so callers get a single, cohesive API and shared cache invalidation.
 */
export function useSavedViewMutations(entityType: ViewEntityType) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: savedViewsKey(entityType) });

  // POST /api/v2/views — persist the current list filters as a named view (Req 7.1, 7.2).
  const createView = useMutation({
    mutationFn: async ({ name, filters }: { name: string; filters: ViewFilters }) => {
      const { data } = await api.post<CreateViewResponse>('/api/v2/views', {
        name,
        entityType,
        filters,
      });
      return data.view;
    },
    onSuccess: invalidate,
  });

  // POST /api/v2/views/:id/apply — resolve a saved view into list query params (Req 7.3, 7.5).
  const applyView = useMutation({
    mutationFn: async (view: SavedView) => {
      const { data } = await api.post<ApplyViewResponse>(
        `/api/v2/views/${encodeURIComponent(view.id)}/apply`,
      );
      return data;
    },
  });

  // DELETE /api/v2/views/:id — remove a view the caller owns.
  const deleteView = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/v2/views/${encodeURIComponent(id)}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { createView, applyView, deleteView };
}
